import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummarySubmissionType,
  enumTransportationMode,
} from '@app/data-layer/enums/entry-summaries';
import {
  ForeignPortModel,
  LocationOfGoodsModel,
  PortModel,
  ShipmentModel,
  StateModel,
  ValidationMessageModel,
} from '@app/data-layer/models/common';
import { EntrySummaryModel } from '@app/data-layer/models/entry-summaries';
import { MarineInsuranceModel } from '@app/data-layer/models/marine-insurance';
import { PaymentBillItemPendingModel } from '@app/data-layer/models/payments';
import { CodeRepository } from '@app/data-layer/repositories';
import {
  ARRIVAL_DATE_MISSING,
  ARRIVAL_DATE_TOO_EARLY,
  EXPORT_DATE_LATER_THAN_ARRIVAL_DATE,
  EXPORT_DATE_MISSING,
  FOREIGN_PORT_MISSING,
  LOCATION_MISSING,
  PORT_OF_ENTRY_MISSING,
  PORT_OF_UNLADING_MISSING,
  STATE_OF_DESTINATION_MISSING,
} from '@constants/entity-validation-messages';
import { notTheSameAsValidator } from '@helpers/custom-validator';
import { DateHelper } from '@helpers/date.helper';
import { EsTransportationModeHelper } from '@helpers/es-transportation-mode.helper';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { CompaniesService } from '@services/companies.service';
import { EntrySummaryStateService, EsOcrQuickFixService } from '@shared/es-mutual/services';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-es-ocr-quick-fix-main-shipment-details',
  templateUrl: './es-ocr-quick-fix-main-shipment-details.component.html',
  styleUrls: ['./es-ocr-quick-fix-main-shipment-details.component.scss'],
})
export class EsOcrQuickFixMainShipmentDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;

  @Input() public shipmentDetailsFields: ISectionFormField[];
  @Input() public fromFields: ISectionFormField[];
  @Input() public toFields: ISectionFormField[];
  @Input() public viaFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public entrySummary: EntrySummaryModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public arrivalMinDate: string;
  public enumTransportationMode = enumTransportationMode;

  private exportMaxDate: string;
  /** #13422: mode the ES had when the step opened — keeps the Truck tile for a Truck ES even without the beta flag. */
  private initialTransportationMode: enumTransportationMode;
  private initialPorts: PortModel[] = [];
  private arrivalDateHelper = new BehaviorSubject<string>('FORM_HELPER.SHIPMENT_ARRIVAL_DATE');

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private codeRepository: CodeRepository,
    private companiesService: CompaniesService,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private entrySummaryStateService: EntrySummaryStateService,
  ) {}

  ngOnInit(): void {
    this.entrySummary = this.esOcrQuickFixService.getEntrySummary();
    this.initialTransportationMode = this.entrySummary.transportationMode;
    const company = this.companiesService.getCompany();
    this.formGroup = this.fh.createEntryShipmentFormGroup(company?.authorizationAgreementAccepted && !!company?.payerUnitNumber);

    this.initUpdatingDatesErrors();
    this.initValidatorChanges();

    this.updateEntryShipmentFormGroup();

    this.setFieldsProperties();

    this.populateDisplayErrors();
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onSubmit(needValidate: boolean = true) {
    let fieldsToSkip = [];
    const allRequiredFormControlKeys = Object.keys(this.formGroup.controls);
    if (this.isAir || this.isTruck) {
      fieldsToSkip.push(...allRequiredFormControlKeys.filter((item) => ['foreignPortOfLadingId'].includes(item)));
    }

    if (this.isTruck) {
      fieldsToSkip.push(...allRequiredFormControlKeys.filter((item) => ['locationCode', 'trip'].includes(item)));
    }

    if (this.isAir || this.isRail) {
      fieldsToSkip.push(...allRequiredFormControlKeys.filter((item) => ['vesselName'].includes(item)));
    }

    if (!this.formGroup.get('differentPortEntry').value) {
      fieldsToSkip.push(...allRequiredFormControlKeys.filter((item) => ['portOfUnladingCode'].includes(item)));
    }

    const invalidFields = this.getInvalidFields(fieldsToSkip);
    if (needValidate && Object.keys(invalidFields).length) {
      this.openMissingFieldsModal(invalidFields);
      return;
    }

    this.createOrUpdateEntrySummary()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data: EntrySummaryModel) => {
          if (
            this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
            data.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED
          ) {
            return this.entrySummaryStateService.copyCompanyPoaToEntryIfNeeded(data).pipe(map(() => data));
          }
          return of(data);
        }),
        switchMap((data: EntrySummaryModel) => {
          if (data.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH) {
            return this.entrySummaryStateService.copyCompanyPunToEntryIfNeeded(data).pipe(map(() => data));
          }
          return of(data);
        }),
        switchMap((data: EntrySummaryModel) => this.entrySummaryStateService.updateDocuments(true).pipe(map(() => data))),
        switchMap((data: EntrySummaryModel) => {
          return this.createOrUpdateShipment(data);
        }),
        switchMap((data: EntrySummaryModel) => this.clearHouseBillIfHidden(data)),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
        switchMap((data: EntrySummaryModel) => combineLatest([of(data), this.entrySummaryStateService.updateInsuranceShipmentDate(data)])),
      )
      .subscribe(([data, _]: [EntrySummaryModel, [MarineInsuranceModel, PaymentBillItemPendingModel]]) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(data);
        this.dataSaved.emit();
      });
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[]): boolean {
    let invalidFields = blockFields.find((field) => this.formGroup.get(field.name)?.invalid || !this.formGroup.get(field.name)?.value);
    return !!invalidFields;
  }

  /**
   * #13422: the mode lives on the ES object (no form control) — mutate it and refresh everything that
   * depends on it: field visibility/labels, mode helpers and the mode validators (cf. #13261 in the edit sidebar).
   */
  public onShipmentTypeSelect(transportationMode: enumTransportationMode) {
    this.entrySummary.transportationMode = transportationMode;

    this.setShipmentDetailsFieldsProperties();
    this.updateModeDependentFieldsProperties();
    this.fh.applyEntryShipmentModeValidators(this.formGroup, transportationMode);
  }

  public get exportHelper(): string {
    if (this.isAir) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_AIR';
    } else if (this.isOcean || this.isRail) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_SHIP_DESKTOP';
    } else if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_TRUCK';
    }
  }

  public get tripLabel(): string {
    switch (this.entrySummary?.transportationMode) {
      case enumTransportationMode.AIR:
        return 'FORM_LABEL.FLIGHT_NUMBER';
      case enumTransportationMode.OCEAN:
        return 'LABELS.VOYAGE_NUMBER';
      case enumTransportationMode.RAIL:
        return 'FORM_LABEL.RAIL_TRIP_NUMBER';
      default:
        break;
    }
  }

  public get isAir(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.AIR;
  }

  public get isOcean(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.OCEAN;
  }

  public get isRail(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.RAIL;
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  /** #13422: Truck is a beta feature (es_trucker_integration); an ES opened as Truck keeps its tile while switching. */
  public get isTruckTileVisible(): boolean {
    return this.companiesService.isEsTruckModeAvailable || this.initialTransportationMode === enumTransportationMode.TRUCK;
  }

  private setShipmentDetailsFieldsProperties() {
    this.shipmentDetailsFields.forEach((field) => {
      switch (field.name) {
        case 'vesselName':
          field.isHidden = this.isAir || this.isRail || this.isTruck;
          break;
        case 'trip':
          field.isHidden = this.isTruck;
          field.label = this.tripLabel;
          if (this.isAir) {
            field.mask = '0{5}';
            field.maskErrorTooltip = 'MASK_ERROR.ONLY_DIGITS';
            field.maxLengthMessage = 'MASK_ERROR.MAX_DIGITS_LENGTH';
          } else {
            field.mask = 'F{5}';
            field.maskErrorTooltip = 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL';
            field.maxLengthMessage = null;
          }
          break;

        default:
          break;
      }
    });
  }

  private createOrUpdateEntrySummary() {
    const form = this.getValidForm();
    if (this.entrySummary && this.entrySummary.id) {
      return this.entrySummaryStateService.updateEntrySummary(form).pipe(takeUntil(this.destroy$));
    } else {
      return this.entrySummaryStateService.createEntrySummary(form).pipe(takeUntil(this.destroy$));
    }
  }

  private createOrUpdateShipment(entrySummary: EntrySummaryModel): Observable<Partial<EntrySummaryModel>> {
    const { foreignPortOfLadingId: prevForeignPortOfLadingId, exportDate: prevExportDate } =
      this.entrySummaryStateService.getSummaryForeignPortOfLadingData(this.entrySummary);
    const { hasSingleExportData, foreignPortOfLadingId, exportDate } = this.formGroup.value;

    const differentPortEntry = entrySummary.portOfEntryCode !== entrySummary.portOfUnladingCode;
    const hasShipmentsWithIT = entrySummary.shipments.some((shipment) => !!(shipment.itDate || shipment.itNumber));

    const isChangedForeignPort = foreignPortOfLadingId !== prevForeignPortOfLadingId;
    const isChangedExportDate = exportDate !== prevExportDate;

    const isNeedUpdateShipments =
      (!hasSingleExportData && (isChangedForeignPort || isChangedExportDate)) || (!differentPortEntry && hasShipmentsWithIT);

    if (isNeedUpdateShipments) {
      if (entrySummary.shipments.length === 0) {
        return this.createShipment({ foreignPortOfLadingId, exportDate }, entrySummary);
      } else {
        const shipmentsToUpdate = entrySummary.shipments
          .filter(
            (shipment) =>
              shipment.foreignPortOfLadingId !== foreignPortOfLadingId ||
              shipment.exportDate !== exportDate ||
              (!differentPortEntry && (shipment.itDate || shipment.itNumber)),
          )
          .map((shipment) => {
            if (!differentPortEntry) {
              shipment.itDate = null;
              shipment.itNumber = null;
            }
            return shipment;
          });
        return this.updateShipments(shipmentsToUpdate, { foreignPortOfLadingId, exportDate }, entrySummary);
      }
    }

    return of({ ...entrySummary, hasSingleExportData });
  }

  private createShipment(data, entrySummary: EntrySummaryModel): Observable<EntrySummaryModel> {
    return this.entrySummaryStateService.createShipment(data).pipe(
      switchMap((shipment) => {
        entrySummary.shipments = [shipment];
        return of(entrySummary);
      }),
    );
  }

  private updateShipments(
    shipmentsToUpdate: ShipmentModel[],
    fieldsToUpdate: Partial<ShipmentModel>,
    entrySummary: EntrySummaryModel,
  ): Observable<EntrySummaryModel> {
    return forkJoin(
      shipmentsToUpdate.map((shipment) => {
        return this.entrySummaryStateService.updateShipment(shipment, fieldsToUpdate);
      }),
    ).pipe(
      switchMap((updatedShipments: ShipmentModel[]) => {
        entrySummary.shipments = this.getUpdatedShipmentsForEntrySummary(updatedShipments, entrySummary);
        return of(entrySummary);
      }),
    );
  }

  private getUpdatedShipmentsForEntrySummary(updatedShipments: ShipmentModel[], entrySummary: EntrySummaryModel) {
    const updatedShipmentsId = updatedShipments.filter((item) => item).map((item) => item.id);
    return entrySummary.shipments.map((item) => {
      if (updatedShipmentsId.includes(item.id)) {
        return updatedShipments.find((updatedShipment) => item.id === updatedShipment.id);
      }
      return item;
    });
  }

  private getValidForm(): Partial<EntrySummaryModel> {
    const form: Partial<EntrySummaryModel> = this.formGroup.value;
    for (const field in form) {
      if (form[field] === '') {
        form[field] = null;
      }
    }
    // #13422: the mode has no form control — send it explicitly instead of relying on the mutated
    // ES object being the same reference the state service spreads into the payload (cf. #13261).
    form.transportationMode = this.entrySummary.transportationMode;

    return form;
  }

  private getInvalidFields(fieldsToSkip?: string[]) {
    if (
      !this.formGroup.get('differentPortEntry').value &&
      this.formGroup.get('portOfEntryCode').value !== this.formGroup.get('portOfUnladingCode').value
    ) {
      this.formGroup.get('portOfUnladingCode').setValue(this.formGroup.get('portOfEntryCode').value);
    }
    const invalidFields = this.fh.validateAllFields(this.formGroup);
    for (const key of fieldsToSkip) {
      delete invalidFields[key];
    }
    if (Object.keys(invalidFields).length) {
      if (this.formGroup.get('hasSingleExportData').value) {
        delete invalidFields['foreignPortOfLadingId'];
        delete invalidFields['exportDate'];
      }
      if (this.entrySummary.transportationMode === enumTransportationMode.AIR && invalidFields['trip']) {
        delete invalidFields['trip'];
        invalidFields['flightNumber'] = 'flightNumber';
      }
      if (Object.keys(invalidFields).length) {
        return invalidFields;
      }
    }

    return {};
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private updateEntryShipmentFormGroup() {
    if (!this.entrySummary.id) {
      return;
    }
    const {
      hasSingleExportData,
      vesselName,
      trip,
      portOfEntryCode,
      portOfUnladingCode,
      arrivalDate,
      locationCode,
      stateOfDestination,
      type,
      submissionType,
      dutiesPaymentType,
      payerUnitNumber,
    } = this.entrySummary;
    const { foreignPortOfLadingId, exportDate } = this.entrySummaryStateService.getSummaryForeignPortOfLadingData(this.entrySummary);
    const entrySummaryToUpdate = {
      trip,
      vesselName: this.isAir ? null : vesselName,
      portOfEntryCode,
      portOfUnladingCode,
      differentPortEntry: portOfEntryCode !== portOfUnladingCode,
      arrivalDate,
      locationCode,
      hasSingleExportData,
      foreignPortOfLadingId: !hasSingleExportData ? foreignPortOfLadingId : null,
      exportDate: !hasSingleExportData && exportDate ? exportDate : null,
      stateOfDestination,
      type,
      submissionType,
      dutiesPaymentType,
      payerUnitNumber,
    };

    if (entrySummaryToUpdate.foreignPortOfLadingId) {
      const foreignPortOfLadingIdField = this.fromFields.find((field) => field.name === 'foreignPortOfLadingId');

      const setInitializedItems = (port: ForeignPortModel) => {
        foreignPortOfLadingIdField.initializedItems = [port];
        foreignPortOfLadingIdField.value = `${port?.code} ${port?.portName} ${port?.countryCode}`;
      };

      this.getCurrentForeignPortOfLading(entrySummaryToUpdate.foreignPortOfLadingId, setInitializedItems);
    }

    if (entrySummaryToUpdate.portOfEntryCode) {
      const portOfEntryCodeField = this.toFields.find((field) => field.name === 'portOfEntryCode');
      const setInitializedItems = (ports: PortModel[]) => {
        const portOfEntry = ports.find((port: PortModel) => port.code === this.entrySummary.portOfEntryCode);
        portOfEntryCodeField.initializedItems = ports;
        portOfEntryCodeField.value = portOfEntry ? `${portOfEntry?.code} ${portOfEntry?.description}` : '';
      };
      this.getCurrentPorts(entrySummaryToUpdate.portOfEntryCode, setInitializedItems);
    }

    if (entrySummaryToUpdate.portOfUnladingCode && entrySummaryToUpdate.portOfEntryCode !== entrySummaryToUpdate.portOfUnladingCode) {
      const portOfUnladingCodeField = this.viaFields.find((field) => field.name === 'portOfUnladingCode');
      const setInitializedItems = (ports: PortModel[]) => {
        portOfUnladingCodeField.initializedItems = ports;
      };
      this.getCurrentPorts(entrySummaryToUpdate.portOfUnladingCode, setInitializedItems);
    }

    if (entrySummaryToUpdate.locationCode) {
      const locationCodeField = this.toFields.find((field) => field.name === 'locationCode');

      const setInitializedItems = (location: LocationOfGoodsModel) => {
        locationCodeField.initializedItems = [location];
        locationCodeField.value = `${location?.code} ${location?.name}`;
      };
      this.getCurrentLocation(entrySummaryToUpdate.locationCode, setInitializedItems);
    }

    if (entrySummaryToUpdate.stateOfDestination) {
      const stateOfDestinationField = this.toFields.find((field) => field.name === 'stateOfDestination');
      const setInitializedItems = (state: StateModel) => {
        stateOfDestinationField.initializedItems = [state];
        stateOfDestinationField.value = state.description;
      };
      this.getCurrentState(entrySummaryToUpdate.stateOfDestination, setInitializedItems);
    }

    const company = this.companiesService.getCompany();

    this.formGroup = this.fh.createEntryShipmentFormGroupWithData(
      entrySummaryToUpdate,
      this.isAir,
      company.authorizationAgreementAccepted && !!company.payerUnitNumber,
    );

    this.initUpdatingDatesErrors();
    this.initValidatorChanges();
    this.initUpdatingArrivalDateHelper();

    if (submissionType === enumEntrySummarySubmissionType.MANUAL) {
      const arrivalDateValue = this.formGroup.get('arrivalDate').value;

      if (arrivalDateValue < DateHelper.addDaysByUTC(DateHelper.startOfTheDayByUTC(DateHelper.now), -7)) {
        this.formGroup.get('arrivalDate').patchValue(null);
      }
    }

    this.updateArrivalMinDate(submissionType);

    this.fh.applyEntryShipmentModeValidators(this.formGroup, this.entrySummary.transportationMode);

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private setFieldsProperties() {
    if (!this.fromFields.length) {
      return;
    }

    this.setShipmentDetailsFieldsProperties();

    this.fromFields.forEach((field) => {
      switch (field.name) {
        case 'foreignPortOfLadingId':
          field.change = this.getForeignPorts();

          if (this.entrySummary?.foreignPortOfLadingId) {
            const setInitializedItems = (port: ForeignPortModel) => {
              field.initializedItems = [port];
              field.value = `${port?.code} ${port?.portName} ${port?.countryCode}`;
            };
            this.getCurrentForeignPortOfLading(this.entrySummary?.foreignPortOfLadingId, setInitializedItems);
          }
          break;
        case 'exportDate':
          field.maxDate = this.exportMaxDate;
          break;

        default:
          break;
      }
    });

    this.toFields.forEach((field) => {
      switch (field.name) {
        case 'locationCode':
          field.change = this.getLocations();
          field.update = this.updateLocation.bind(this);
          if (this.entrySummary?.locationCode) {
            const setInitializedItems = (location: LocationOfGoodsModel) => {
              field.initializedItems = [location];
              field.value = `${location?.code} ${location?.name}`;
            };
            this.getCurrentLocation(this.entrySummary?.locationCode, setInitializedItems);
          }
          break;
        case 'portOfEntryCode':
          field.change = this.getPorts();

          if (this.entrySummary?.portOfEntryCode) {
            const setInitializedItems = (ports: PortModel[]) => {
              const portOfEntry = ports.find((port: PortModel) => port.code === this.entrySummary.portOfEntryCode);
              field.initializedItems = ports;
              field.value = portOfEntry ? `${portOfEntry?.code} ${portOfEntry?.description}` : '';
            };
            this.getCurrentPorts(this.entrySummary?.portOfEntryCode, setInitializedItems);
          }
          break;
        case 'stateOfDestination':
          field.change = this.getStates();
          if (this.entrySummary?.stateOfDestination) {
            const setInitializedItems = (state: StateModel) => {
              field.initializedItems = [state];
              field.value = state.description;
            };
            this.getCurrentState(this.entrySummary?.portOfUnladingCode, setInitializedItems);
          }
          break;
        case 'arrivalDate':
          field.minDate = this.arrivalMinDate;
          break;

        default:
          break;
      }
    });

    this.viaFields.forEach((field) => {
      switch (field.name) {
        case 'portOfUnladingCode':
          field.change = this.getPorts();

          if (this.entrySummary?.portOfUnladingCode && this.entrySummary?.portOfEntryCode !== this.entrySummary?.portOfUnladingCode) {
            const setInitializedItems = (ports: PortModel[]) => {
              field.initializedItems = ports;
            };
            this.getCurrentPorts(this.entrySummary?.portOfUnladingCode, setInitializedItems);
          }
          break;

        default:
          break;
      }
    });

    this.updateModeDependentFieldsProperties();
  }

  /**
   * #13422: field properties that follow the transportation mode — applied at init and on every tile switch
   * (the shipment-details block itself is handled by setShipmentDetailsFieldsProperties).
   */
  private updateModeDependentFieldsProperties(): void {
    this.fromFields.forEach((field: ISectionFormField) => {
      switch (field.name) {
        case 'foreignPortOfLadingId':
          field.isHidden = this.isAir || this.isTruck;
          break;
        case 'exportDate':
          field.helper = this.exportHelper;
          break;

        default:
          break;
      }
    });

    this.toFields.forEach((field: ISectionFormField) => {
      switch (field.name) {
        case 'locationCode':
          field.helper = this.locationHelper;
          break;
        case 'stateOfDestination':
          field.isShowHelper = this.isTruck;
          field.helper = this.stateHelper;
          break;
        case 'arrivalDate':
          field.helper = this.arrivalDateHelperStr;
          break;

        default:
          break;
      }
    });
  }

  /** #13422: House bill is hidden for Rail/Truck but lives on the shipment — null it out after a mode change (cf. #13261). */
  private clearHouseBillIfHidden(entrySummary: EntrySummaryModel): Observable<Partial<EntrySummaryModel>> {
    const shipmentsWithHouseBill: ShipmentModel[] = EsTransportationModeHelper.getShipmentsWithStaleHouseBill(entrySummary);
    if (shipmentsWithHouseBill.length === 0) {
      return of(entrySummary);
    }
    return this.updateShipments(shipmentsWithHouseBill, EsTransportationModeHelper.EMPTY_HOUSE_BILL, entrySummary);
  }

  private populateDisplayErrors() {
    Object.keys(this.formGroup.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'vesselName':
        case 'trip':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName));
          break;
        case 'foreignPortOfLadingId':
          this.displayErrors.set(fieldName, this.foreignPortErrorText);
          break;
        case 'exportDate':
          this.displayErrors.set(fieldName, this.exportDateErrorText);
          break;
        case 'locationCode':
          this.displayErrors.set(fieldName, this.locationErrorText);
          break;
        case 'portOfEntryCode':
          this.displayErrors.set(fieldName, this.entryErrorText);
          break;
        case 'portOfUnladingCode':
          this.displayErrors.set(fieldName, this.unladingErrorText);
          break;
        case 'stateOfDestination':
          this.displayErrors.set(fieldName, this.destinationStateErrorText);
          break;
        case 'arrivalDate':
          this.displayErrors.set(fieldName, this.arrivalDateErrorText);
          break;

        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = {
      vesselName: {
        required: 'FORM_ERROR.REQUIRED',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      trip: {
        required: 'FORM_ERROR.REQUIRED',
      },
      portOfEntryCode: {
        required: 'FORM_ERROR.REQUIRED',
        notTheSameAsValidator: 'FORM_ERROR.PORT_OF_ENTRY_SAME_AS_PORT_OF_UNLADING',
      },
      portOfUnladingCode: {
        required: 'FORM_ERROR.REQUIRED',
        notTheSameAsValidator: 'FORM_ERROR.PORT_OF_UNLADING_SAME_AS_PORT_OF_ENTRY',
      },
      exportDate: {
        required: 'FORM_ERROR.REQUIRED',
      },
      arrivalDate: {
        required: 'FORM_ERROR.REQUIRED',
      },
      locationCode: {
        required: 'FORM_ERROR.REQUIRED',
      },
      stateOfDestination: {
        required: 'FORM_ERROR.REQUIRED',
      },
      foreignPortOfLadingId: {
        required: 'FORM_ERROR.REQUIRED',
      },
    };
  }

  private fieldMissingErrorText(fieldName: string): string {
    return !this.formGroup.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private getForeignPorts() {
    return this.codeRepository.searchForeignPort.bind(this.codeRepository);
  }

  private getLocations() {
    return this.codeRepository.searchLocations.bind(this.codeRepository);
  }

  private getPorts() {
    return this.searchPlaceOfDelivery.bind(this);
  }

  private getStates() {
    return this.codeRepository.searchState.bind(this.codeRepository, 'US');
  }

  private getCurrentForeignPortOfLading(id: number, callback?: (port: ForeignPortModel) => void) {
    this.codeRepository
      .getForeignPort(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((port) => {
        callback(port);
      });
  }

  private getCurrentLocation(code: string, callback?: (location: LocationOfGoodsModel) => void) {
    this.codeRepository
      .getLocation(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe((location) => {
        callback(location);
      });
  }

  private getCurrentPorts(code: string, callback?: (ports: PortModel[]) => void) {
    if (this.initialPorts.findIndex((port) => port.code === code) === -1) {
      this.codeRepository
        .getDomesticPort(code)
        .pipe(takeUntil(this.destroy$))
        .subscribe((port) => {
          this.initialPorts = UtilsHelper.concat<PortModel>(this.initialPorts, port);

          if (!callback) {
            return;
          }
          callback(this.initialPorts);
        });
    } else {
      this.initialPorts = cloneDeep(this.initialPorts); // need update the link for array in case if port location was founded early
    }
  }

  private getCurrentState(code: string, callback?: (ports: StateModel) => void) {
    this.codeRepository
      .getState(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        callback(state);
      });
  }

  private updateExportMaxDate() {
    const exportMaxDate: string = this.formGroup.get('arrivalDate').value;
    const exportDateField = this.fromFields.find((field) => field.name === 'exportDate');
    if (exportMaxDate !== this.exportMaxDate && !!exportDateField) {
      this.exportMaxDate = exportMaxDate;
      exportDateField.maxDate = this.exportMaxDate;
    }
  }

  private updateArrivalMinDate(submissionType: enumEntrySummarySubmissionType) {
    const arrivalMinDate: string = this.entrySummaryStateService.arrivalMinDate(
      this.entrySummary,
      this.formGroup.get('exportDate').value,
      this.formGroup.get('hasSingleExportData').value,
      submissionType,
    );
    if (arrivalMinDate !== this.arrivalMinDate) {
      this.arrivalMinDate = arrivalMinDate;
    }
  }

  private initUpdatingArrivalDateHelper(): void {
    this.formGroup
      .get('arrivalDate')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value < DateHelper.addDaysByUTC(DateHelper.startOfTheDayByUTC(DateHelper.now), -7)) {
          this.arrivalDateHelper.next('FORM_HELPER.ARRIVAL_DATE_LESS_7_DAYS');
        } else {
          this.arrivalDateHelper.next('FORM_HELPER.SHIPMENT_ARRIVAL_DATE');
        }
      });
  }

  private initUpdatingDatesErrors(): void {
    merge(
      this.formGroup.get('arrivalDate').valueChanges,
      this.formGroup.get('arrivalDate').statusChanges,
      this.formGroup.get('exportDate').valueChanges,
      this.formGroup.get('exportDate').statusChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateExportMaxDate();
        this.updateArrivalMinDate(this.formGroup.get('submissionType').value);
      });
  }

  private initValidatorChanges() {
    this.formGroup
      .get('differentPortEntry')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const portOfEntryCode = this.formGroup.get('portOfEntryCode');
        const portOfUnladingCode = this.formGroup.get('portOfUnladingCode');

        portOfEntryCode.clearValidators();
        portOfUnladingCode.clearValidators();
        if (value) {
          portOfEntryCode.setValidators([Validators.required, notTheSameAsValidator('portOfUnladingCode', 'notTheSameAsValidator')]);
          portOfUnladingCode.setValidators([Validators.required, notTheSameAsValidator('portOfEntryCode', 'notTheSameAsValidator')]);
        } else {
          portOfUnladingCode.clearValidators();
          portOfEntryCode.setValidators(Validators.required);
        }
        if (value && portOfUnladingCode.value && portOfUnladingCode.value === portOfEntryCode.value) {
          portOfUnladingCode.patchValue(null);
        } else if (!value && portOfUnladingCode.value !== portOfEntryCode.value) {
          portOfUnladingCode.patchValue(portOfEntryCode.value);
        }
        portOfEntryCode.updateValueAndValidity();
        portOfUnladingCode.updateValueAndValidity();
      });
  }

  private searchPlaceOfDelivery(term: string) {
    return this.formGroup.get('submissionType').value === enumEntrySummarySubmissionType.BROKER_ASSISTED
      ? this.codeRepository.searchDomesticPorts(term)
      : this.entrySummaryStateService.searchPlaceOfDelivery(term);
  }

  private updateLocation(location: LocationOfGoodsModel) {
    if (location && location.portCode) {
      const portOfEntryCodeField = this.toFields.find((field) => field.name === 'portOfEntryCode');
      const setInitializedPortItems = (ports: PortModel[]) => {
        const portOfEntry = ports.find((port: PortModel) => port.code === this.entrySummary.portOfEntryCode);
        portOfEntryCodeField.initializedItems = ports;
        portOfEntryCodeField.value = portOfEntry ? `${portOfEntry?.code} ${portOfEntry?.description}` : '';
        this.formGroup.get('portOfEntryCode').patchValue(location.portCode);
      };

      const stateOfDestinationField = this.toFields.find((field) => field.name === 'stateOfDestination');
      const setInitializedStateItems = (state: StateModel) => {
        stateOfDestinationField.initializedItems = [state];
        stateOfDestinationField.value = state.description;
        this.formGroup.get('stateOfDestination').patchValue(location.stateCode);
      };

      this.getCurrentState(location.stateCode, setInitializedStateItems);
      this.getCurrentPorts(location.portCode, setInitializedPortItems);
    }
  }

  private get foreignPortErrorText(): string {
    const firstShipment: ShipmentModel = this.entrySummary?.shipments[0];
    const error: ValidationMessageModel = firstShipment?.validationMessages?.find(
      (item: ValidationMessageModel) => item.text === FOREIGN_PORT_MISSING,
    );

    return error ? `BACK_ERROR.FOREIGN_PORT_MISSING` : null;
  }

  private get exportDateErrorText(): string {
    let error: string = null;
    if (!this.formGroup.get('exportDate')?.value) {
      error = EXPORT_DATE_MISSING;
    } else if (this.findShipmentError(EXPORT_DATE_LATER_THAN_ARRIVAL_DATE)) {
      error = EXPORT_DATE_LATER_THAN_ARRIVAL_DATE;
    }

    return error ? `BACK_ERROR.${error}` : null;
  }

  private get locationErrorText(): string {
    const error: string = this.findError(LOCATION_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  private get unladingErrorText(): string {
    const error: string = this.findError(PORT_OF_UNLADING_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  private get entryErrorText(): string {
    const error: string = this.findError(PORT_OF_ENTRY_MISSING);

    return error != null ? `BACK_ERROR.${error}` : null;
  }

  private get destinationStateErrorText(): string {
    const error: string = this.findError(STATE_OF_DESTINATION_MISSING);

    return error ? `BACK_ERROR.${error}` : null;
  }

  private get arrivalDateErrorText(): string {
    const error: string = this.findError(ARRIVAL_DATE_MISSING) || this.findError(ARRIVAL_DATE_TOO_EARLY);

    return error ? `BACK_ERROR.${error}` : null;
  }

  private get stateHelper(): string {
    if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_STATE_TRUCK';
    } else {
      return '';
    }
  }

  private get locationHelper(): string {
    return this.isTruck ? 'FORM_HELPER.LOCATION_OF_GOODS_TRUCK' : 'FORM_HELPER.LOCATION_OF_GOODS';
  }

  private get arrivalDateHelperStr(): string {
    if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_ARRIVAL_DATE_TRUCK';
    } else {
      return this.arrivalDateHelper.getValue();
    }
  }

  private findError(field: string): string {
    const errors: ValidationMessageModel[] = this.entrySummary?.validationMessages || [];

    return errors.find((item: ValidationMessageModel) => item.text === field)?.text;
  }

  private findShipmentError(field: string): string {
    const errors: ValidationMessageModel[] = this.entrySummary?.shipments?.[0]?.validationMessages || [];

    return errors.find((item: ValidationMessageModel) => item.text === field)?.text;
  }
}
